import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { statusMeta, sourceIcon, fmtMoney, fmtDate, STATUSES } from "../lib/constants";
import {
  Users,
  TrendingUp,
  DollarSign,
  CalendarClock,
  Activity,
  Trophy,
  PhoneCall,
} from "lucide-react";
import { motion } from "framer-motion";

const StatCard = ({ icon: Icon, label, value, accent, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="rounded-2xl border border-border bg-card p-5 hover:-translate-y-0.5 transition-transform"
    data-testid={`stat-${label.toLowerCase().replace(/ /g, "-")}`}
  >
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-widest text-muted-foreground font-mono">{label}</p>
      <Icon className={`h-4 w-4 ${accent}`} />
    </div>
    <p className="mt-3 text-3xl font-extrabold font-display">{value}</p>
  </motion.div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [scope, setScope] = useState(user?.role === "admin" ? "all" : "mine");
  const mine = scope === "mine";
  const { data: stats } = useQuery({ queryKey: ["stats", scope], queryFn: () => api.get("/dashboard/stats", { params: { mine } }).then((r) => r.data) });
  const { data: recent } = useQuery({ queryKey: ["recent", scope], queryFn: () => api.get("/dashboard/recent", { params: { mine } }).then((r) => r.data) });
  const { data: reps = [] } = useQuery({ queryKey: ["reps"], queryFn: () => api.get("/dashboard/reps").then((r) => r.data) });

  const s = stats || { by_status: {}, by_source: {} };

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Sales Command Center</h1>
          <p className="text-muted-foreground mt-1">Track every lead from first touch to closed deal.</p>
        </div>
        <div className="flex rounded-lg border border-border p-1 bg-card w-fit">
          {["mine", "all"].map((v) => (
            <button
              key={v}
              onClick={() => setScope(v)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${scope === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`scope-${v}`}
            >
              {v === "mine" ? "My pipeline" : "Whole team"}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Leads" value={s.total_leads ?? 0} accent="text-blue-400" delay={0} />
        <StatCard icon={TrendingUp} label="Conversion" value={`${s.conversion_rate ?? 0}%`} accent="text-purple-400" delay={0.05} />
        <StatCard icon={DollarSign} label="Pipeline" value={fmtMoney(s.pipeline_value)} accent="text-primary" delay={0.1} />
        <StatCard icon={CalendarClock} label="Follow-ups" value={s.pending_followups ?? 0} accent="text-amber-400" delay={0.15} />
      </div>

      <div className="rounded-2xl border border-border bg-card p-6" data-testid="rep-leaderboard">
        <h3 className="font-display text-lg font-semibold mb-5 flex items-center gap-2">
          <Trophy className="h-4 w-4 text-primary" /> Rep leaderboard
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="py-2 pr-4 font-medium">Rep</th>
                <th className="py-2 px-3 font-medium text-right">Assigned</th>
                <th className="py-2 px-3 font-medium text-right">Won</th>
                <th className="py-2 px-3 font-medium text-right">Conv.</th>
                <th className="py-2 px-3 font-medium text-right">Calls</th>
                <th className="py-2 px-3 font-medium text-right">Texts</th>
              </tr>
            </thead>
            <tbody>
              {reps.map((r, i) => (
                <tr key={r.email} className="border-b border-border last:border-0" data-testid={`rep-row-${i}`}>
                  <td className="py-2.5 pr-4">
                    <div className="flex items-center gap-2">
                      {i === 0 && r.won > 0 && <Trophy className="h-3.5 w-3.5 text-amber-400" />}
                      <span className="font-medium">{r.name || r.email.split("@")[0]}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{r.role}</span>
                    </div>
                  </td>
                  <td className="py-2.5 px-3 text-right">{r.assigned}</td>
                  <td className="py-2.5 px-3 text-right text-emerald-400 font-medium">{r.won}</td>
                  <td className="py-2.5 px-3 text-right">{r.conversion}%</td>
                  <td className="py-2.5 px-3 text-right"><span className="inline-flex items-center gap-1 text-muted-foreground"><PhoneCall className="h-3 w-3" />{r.calls}</span></td>
                  <td className="py-2.5 px-3 text-right text-muted-foreground">{r.sms}</td>
                </tr>
              ))}
              {reps.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No reps yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 min-w-0 rounded-2xl border border-border bg-card p-6">
          <h3 className="font-display text-lg font-semibold mb-5">Pipeline breakdown</h3>
          <div className="space-y-4">
            {STATUSES.map((st) => {
              const count = s.by_status?.[st.key] || 0;
              const total = s.total_leads || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={st.key}>
                  <div className="flex items-center justify-between text-sm mb-1.5">
                    <span className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${st.dot}`} />
                      {st.label}
                    </span>
                    <span className="text-muted-foreground">{count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className={`h-full ${st.dot}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="min-w-0 rounded-2xl border border-border bg-card p-6">
          <h3 className="font-display text-lg font-semibold mb-5 flex items-center gap-2">
            <Activity className="h-4 w-4 text-primary" /> Recent activity
          </h3>
          <div className="space-y-4 max-h-80 overflow-y-auto">
            {(recent || []).length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
            {(recent || []).map((a) => (
              <div key={a.id} className="text-sm border-b border-border pb-3 last:border-0">
                <p className="font-medium">{a.lead_name}</p>
                <p className="text-muted-foreground text-xs mt-0.5 capitalize break-words [overflow-wrap:anywhere]">{a.type.replace("_", " ")}: {a.body}</p>
                <p className="text-muted-foreground text-xs mt-0.5">{fmtDate(a.created_at)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <h3 className="font-display text-lg font-semibold mb-5">Leads by source</h3>
        <div className="flex flex-wrap gap-3">
          {Object.keys(s.by_source || {}).length === 0 && <p className="text-sm text-muted-foreground">No sources yet.</p>}
          {Object.entries(s.by_source || {}).map(([src, count]) => {
            const Icon = sourceIcon(src);
            return (
              <div key={src} className="flex items-center gap-2.5 rounded-xl border border-border px-4 py-3">
                <Icon className="h-5 w-5 text-primary" />
                <div>
                  <p className="text-sm font-semibold capitalize">{src}</p>
                  <p className="text-xs text-muted-foreground">{count} leads</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
